import React, { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Tooltip } from "@mui/material";
import ElipsisCell from "../../../../components/TableBox/Cells/EllipsisCell";
import ElipsisText from "../../../../components/ElipsisText";
import Badge from "../../../../components/Badge";
import { RowClassName, PermissionModulesEnum, PermissionActionsEnum } from "../../../../enums";
import { TransactionStatus } from "../../../../interfaces/transaction.interface"
import { Edit as EditInterface } from "../../../../interfaces/invoice.interface";
import { TableContext } from "../../../../contexts/TableContext";
import { AuthContext } from "../../../../contexts/AuthContext";
import { VALIDATION_FIELD_MAPPING } from "../../../../utils/appConstant";
import Checkbox from "../../../../components/Checkbox";
import { hasResourseAccess, formatDollarAmount } from "../../../../utils/methods";

interface RowProps {
  item: any;
  className?: RowClassName;
  formData: EditInterface;
  view: boolean;
  onInputChange: (e: any, name: string, type: string, id?: string) => void;
  onOpen: (paramId: string, mode: "view" | "edit") => void;
}

const Row: React.FC<RowProps> = ({ item, className, formData, view, onInputChange, onOpen }) => {
  const { t } = useTranslation();
  const { headCells } = useContext(TableContext);
  const { permissions, isSystemAdmin } = useContext(AuthContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Transaction]: [PermissionActionsEnum.Update] }] });

  const handleDateClick = useCallback((id: string) => {
    onOpen(id, (hasEditAccess && !view) ? 'edit' : 'view');
  }, [view])

  const getStatusVariant = (status: TransactionStatus) => {
    if (status === VALIDATION_FIELD_MAPPING.unBilled) return "secondary";
    else if (status === VALIDATION_FIELD_MAPPING.invoice) return "warning"
    else return "info"
  }

  const transactionDateNode = (
    <div>
      <ElipsisText 
        width="100%" 
        text={item.date ?? "-"}
        className="h6 mb-1 text-decoration-underline hyperlink"
        onClick={() => handleDateClick(item.id)}
      />
      {
        item.reservation && (
          <Tooltip
            title={item.reservation.name}
            arrow
            classes={{ tooltip: "tooltip" }}
          >
            <div className="text-decoration-underline fst-italic">{t("invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.reservationDetails")}</div>
          </Tooltip>
        )
      }
    </div>
  );

  const debitCreditNode = (
    <div className="d-flex align-items-center">
      <i className={clsx(`ri-arrow-${item.debit_credit === 'dr' ? 'down' : 'up'}-line ri-16px text-${item.debit_credit === 'dr' ? 'primary' : 'success'} me-1`)}></i>
      <Badge text={item.debit_credit_label} variant={item.debit_credit === 'dr' ? 'primary' : 'success'} />
    </div>
  );

  const transactionTypeNode = (
    <div>
      <ElipsisText width="100%" text={item.transaction_type?.name || "-"} className="h6 mb-1" />
      {item?.vendor && <ElipsisText width="100%" text={t("invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.vendor", { vendorName: item?.vendor?.name || "-" })} />}
    </div>
  )

  const actualAmountNode = (
    <div className="fw-semibold d-flex align-items-center gap-2">
      <span>{item.actual_amount ? formatDollarAmount(item.actual_amount) : '-'}</span>
    </div>
  );

  const amountNode = (
    <div className="fw-semibold d-flex align-items-center gap-2">
      <span>{item.formatted_sub_total_amount ? formatDollarAmount(item.formatted_sub_total_amount) : '-'}</span>
      {!item.reconciled && !!item.reconciled_by_user && (
        <Tooltip
          title={t("invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.reviewedBy", { reviewedBy: item.reconciled_by_user?.name || "-" })}
          arrow
          classes={{ tooltip: "tooltip" }}
        >
          <i className="ri-spam-line text-warning ri-20px" />
        </Tooltip>
      )}
    </div>
  );

  const updatedNode = (
    <Tooltip
      title={
        <>
          <div>{t('invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.lastUpdatedBy')} {item.updater?.name || "-"}</div>
          <div>{t('invoices.editPreviewPayment.pageTexts.form.detailsTab.transactions.lastUpdated')} {item.updated_at || "-"}</div>
        </>
      }
      arrow
      classes={{
        tooltip: 'tooltip',
      }}
    >
      <i className="ri-timer-line ri-20px"></i>
    </Tooltip>
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={item.id} width={headCell.width}>
              <div className="form-floating form-floating-outline">
                <Checkbox
                  variant='primary'
                  className='d-checkbox mb-0'
                  checked={formData.selectedTransactions.includes(item?.id)}
                  disabled={view}
                  name={VALIDATION_FIELD_MAPPING.actions}
                  onChange={(_: any, name: string) => onInputChange(_, name, 'transactions', item?.id)}
                />
              </div>
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.transactionDate) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={transactionDateNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.debitCredit) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={debitCreditNode}
            />
          )
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.transactionType) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={transactionTypeNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.transactionDetails) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item?.transaction_details ?? "-"}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actualAmount) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={actualAmountNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.amount) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={amountNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <td key={headCell.id}>
              <Badge
                text={item.transaction_status_label || "-"}
                variant={getStatusVariant(item.transaction_status)}
              />
            </td>
          )
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.lastUpdated) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={updatedNode}
            />
          );
        }

        return (<ElipsisCell key={headCell.id} width={headCell.width} text={item[headCell.id] ?? "-"} />);
      })}
    </tr>
  );
};

export default Row;
